import config from '@/config'
import { isRequired } from '@shared/utils/CustomErrors'

import useBackend from '@/hooks/useBackend'
import useBrowser from '@shared/hooks/useBrowser'
import { selectedLanguage } from '@/plugins/i18n'

function petLeadsAPI() {
  // HOOKS
  const { doPost, doGet, doPut, setup, buildURL } = useBackend()
  const { isSafari } = useBrowser()

  // INIT
  setup(config.apiPath, config.microserviceBaseUrl)

  // METHODS
  /**
   * createPetLead sends a POST-request to /leads to create a new lead
   * @param {LeadDataDto} payload
   * @returns {Promise<LeadDataDto>} created pet lead and generated lead code
   */
  async function createPetLead(payload) {
    const response = await doPost({
      url: '/leads',
      data: payload,
    })
    return response.data
  }

  /**
   * getPetLeadByCode sends a GET-request to /leads/{leadCode} to get an existing lead
   * @param {string} leadCode
   * @returns {Promise<LeadDataDto>} pet lead data for a lead code
   */
  async function getPetLeadByCode(leadCode) {
    const response = await doGet({
      url: `/leads/${leadCode}`,
    })

    return response.data
  }

  /**
   * updatePetLead sends a PUT-request to /leads/{leadCode} to update an existing lead
   * @param {string} leadCode
   * @param {LeadDataDto} payload
   * @returns {Promise<LeadDataDto>} updated pet lead for a lead code
   */
  async function updatePetLead(leadCode, payload) {
    const response = await doPut({
      url: `/leads/${leadCode}`,
      data: payload,
    })

    return response.data
  }

  /**
   * submitPetLead sends a POST-request to /leads/{leadCode}/submit to submit the final lead
   * @param {string} leadCode
   * @param {LeadDataDto} payload
   * @returns {Promise<LeadDataDto>} submit pet lead for a lead code
   */
  async function submitPetLead(leadCode, payload) {
    const response = await doPost({
      url: `/leads/${leadCode}/submit`,
      data: payload,
    })

    return response.data
  }

  /**
   * submitPetLeadOnlyForTesting sends a POST-request to /leads/{leadCode}/submit-onlyForTesting to
   * submit the final lead with an overridable policy start date (non-production only).
   * @param {string} leadCode
   * @param {LeadDataDto} payload
   * @param {string} overwritePolicyStartDate - the overriding policy start date (ISO yyyy-MM-dd),
   *   sent as a query param and mapped to the LocalDate @RequestParam on the backend
   * @returns {Promise<void>}
   */
  async function submitPetLeadOnlyForTesting(leadCode, payload, overwritePolicyStartDate) {
    await doPost({
      url: `/leads/${leadCode}/submit-onlyForTesting`,
      data: payload,
      config: {
        params: { overwritePolicyStartDate },
      },
    })
  }

  /**
   * sendSummary sends a POST-request to /leads/{leadCode}/send-summary to send the summary in email
   * @param {string} leadCode
   * @param {LeadDataDto} payload
   * @returns {Promise<LeadDataDto>} Saved summary lead for a lead code
   */
  async function sendSummary(leadCode = isRequired('leadCode'), payload) {
    const response = await doPost({
      url: `/leads/${leadCode}/send-summary`,
      data: payload,
    })

    return response
  }

  /**
   * unsubscribeFromReminderEmails sends a POST-request to /public/v1/leads/{leadCode}/unsubscribe-reminder-emails
   * @param {string} leadCode
   * @returns {Promise} unsubscribe from reminder emails for a lead code
   */
  async function unsubscribeFromReminderEmails(leadCode = isRequired('leadCode')) {
    return await doPost({ url: `/leads/${leadCode}/unsubscribe-reminder-emails`})
  }

  /**
   * getCustomerInfoDocument opens the document URL directly, letting the browser handle redirects
   * @param {string} leadCode
   * @returns {void} Opens the document in a new window/tab
   */
  function getCustomerInfoDocument({ leadCode = isRequired('leadCode') }) {
    try {
      // Open about:blank first to hide the backend URL initially
      const newWindow = window.open('about:blank', isSafari() ? '_top' : '_blank')
      if (newWindow) {
        const documentUrl = buildURL(`/leads/${leadCode}/CustomerInfo.pdf?lang=${selectedLanguage.value}`)
        newWindow.location.href = documentUrl
      }
    } catch (error) {
      console.error('Error opening customer info document:', error)
    }
  }

  return {
    createPetLead,
    getPetLeadByCode,
    submitPetLead,
    submitPetLeadOnlyForTesting,
    updatePetLead,
    sendSummary,
    unsubscribeFromReminderEmails,
    getCustomerInfoDocument
  }
}

export default petLeadsAPI()
